// ---------------------------------------------------------------------------
// Business-analytics (userbi) telemetry registry.
//
// The TSL `userbi` API takes freeform strings for panelType, panelRegion,
// actionScenario and actionScenarioType. To keep analytics consistent and
// greppable, this app requires those values to come from the enums below.
// Register a new value by adding a member to the relevant enum — its string
// value is what ships to Aria.
// ---------------------------------------------------------------------------

// Inspired by https://domoreexp.visualstudio.com/Teamspace/_search?action=contents&text=PanelRegions%20ext%3Ajson&type=code&lp=code-Team&filters=ProjectFilters%7BTeamspace%7DRepositoryFilters%7Bteams-modular-packages%7D&pageSize=25&result=DefaultCollection/Teamspace/teams-modular-packages/GBmaster//packages/core/services/core-services-telemetry-types/definitions/common.json

/** UI surfaces that emit analytics (maps to SDK `Panel.Type`). */
export enum PanelType {
  HomePage = 'home_page',
  EvaluationTenantsPage = 'evaluation_tenants_page',
  AgentsPage = 'agents_page',
  GradersPage = 'graders_page',
  JudgeFactoryPage = 'judge_factory_page',
  JudgeFactoryCreationPage = 'judge_factory_creation_page',
  EvaluationTemplatesPage = 'evaluation_templates_page',
  EvaluationRunsPage = 'evaluation_runs_page',
  TrendsPage = 'trends_page',
  ReportTrajectory = 'report_trajectory',
  DatasetsPage = 'datasets_page',
  AnnotationEditorPage = 'annotation_editor_page',
  TestSetsPage = 'test_sets_page',
  PermissionsPage = 'permissions_page',
  AgentDebugPage = 'agent_debug_page',
  SharedSignalsPage = 'shared_signals_page',
  WorkspaceDrawer = 'workspace_drawer',
  CallerPage = 'caller_page',
  SigsTestPage = 'sigs_test_page',
}

/** Regions within a surface (maps to SDK `Panel.Region`). */
export enum PanelRegion {
  Header = 'header',
  Footer = 'footer',
  Overlay = 'overlay',
  Main = 'main',
  Top = 'top',
  Left = 'left',
  Right = 'right',
  Bottom = 'bottom',
}

/** Stable view names recorded within a panel. */
export enum ViewType {
  Overview = 'overview',
  List = 'list',
  Detail = 'detail',
  Create = 'create',
  Edit = 'edit',
  Evaluate = 'evaluate',
  Report = 'report',
  Run = 'run',
  Tool = 'tool',
}

/** Resource kinds acted on by users. */
export enum EntityType {
  Agent = 'agent',
  Grader = 'grader',
  Judge = 'judge',
  EvaluationTemplate = 'evaluation_template',
  EvaluationRun = 'evaluation_run',
  Dataset = 'dataset',
  DatasetEntry = 'dataset_entry',
  AnnotationSet = 'annotation_set',
  AnnotationData = 'annotation_data',
  TestSet = 'test_set',
  TestSetEntry = 'test_set_entry',
  Permission = 'permission',
  SyntheticRun = 'synthetic_run',
  Workspace = 'workspace',
  SharedSignal = 'shared_signal',
}

/** Mutations and submitted workflows recorded by operation telemetry. */
export enum OperationType {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
  Publish = 'publish',
  Demote = 'demote',
  Snapshot = 'snapshot',
  Validate = 'validate',
  Queue = 'queue',
  Promote = 'promote',
  Save = 'save',
  Generate = 'generate',
  Download = 'download',
  Cancel = 'cancel',
}

/** Logical action scenarios (maps to SDK `Action.Scenario`). */
export enum ActionScenario {
  SubmitJudgeCreation = 'submit_judge_creation',
  OpenDatasetViewer = 'open_dataset_viewer',
  AnnotateDatasetEntry = 'annotate_dataset_entry',
  AddExpectedAction = 'add_expected_action',
  SwitchWorkspace = 'switch_workspace',
  CopySharedSignalLink = 'copy_shared_signal_link',
  OpenSharedSignal = 'open_shared_signal',
  OpenReportTrajectory = 'open_report_trajectory',
  DismissReportTrajectory = 'dismiss_report_trajectory',
}

export type OperationActionScenario = `${EntityType}_${OperationType}`;
export type OperationScenarioName = `operation_${EntityType}_${OperationType}`;

export function getOperationActionScenario(
  entityType: EntityType,
  operation: OperationType,
): OperationActionScenario {
  return `${entityType}_${operation}`;
}

export function getOperationScenarioName(
  entityType: EntityType,
  operation: OperationType,
): OperationScenarioName {
  return `operation_${entityType}_${operation}`;
}

/** Coarse action categories (maps to SDK `Action.ScenarioType`). */
export enum ActionScenarioType {
  Back = 'back',
  CloseDialog = 'close_dialog',
  OpenDialog = 'open_dialog',
  Open = 'open',
  Save = 'save',
  Submit = 'submit',
}

export enum ActionOutcome {
  Requested = 'requested',
  Success = 'success',
  Failure = 'failure',
}

/** Module names within a surface */
export enum ModuleName {
  Cancel = 'cancel',
  Link = 'link',
  SubmitButton = 'submit_button',
}
